import axios from "axios";

// Absolute backend origin (Emergent preview/prod) or "" for same-origin
// deployments where the host routes /api/* to the backend service.
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const ANSWER_SCORING_TIMEOUT_MS = 75000;
const ANSWER_RECOVERY_ATTEMPTS = 3;
const ANSWER_RECOVERY_DELAY_MS = 2000;

// Some broad occupational families contain both accessible frontline/support
// roles and highly specialized or regulated professions. When the candidate has
// NOT supplied a specialty, keep AI-generated practice JDs in the broad,
// representative lane instead of silently choosing a degree/licence profession.
// User-supplied specialty/context always wins and bypasses this default guidance.
const SAMPLE_JD_DEFAULT_ROLE_GUIDANCE = {
  manufacturing:
    "No specialty was supplied. Choose a broadly representative manufacturing/production role such as Production Operator, Manufacturing Associate, Production Technician, Assembly Technician, or Production Coordinator. Do not default to Production Engineer, Manufacturing Engineer, Industrial Engineer, or another engineering profession unless the user explicitly asks for engineering.",
  saude:
    "No specialty was supplied. Choose a broadly accessible healthcare support, administrative, patient-service, or technician role appropriate to the selected level. Do not default to physician, registered nurse, pharmacist, therapist, or another licensed/regulated clinical profession unless the user explicitly asks for that profession.",
  legal_compliance:
    "No specialty was supplied. Choose a broadly representative legal-support or compliance role such as Legal Assistant, Compliance Coordinator, Compliance Analyst, or Contract Administrator. Do not default to lawyer, attorney, paralegal where licensing is required, or another regulated legal profession unless the user explicitly asks for it.",
  trades_construction:
    "No specialty was supplied. Choose a broadly representative construction or skilled-trades support role appropriate to the selected level. Do not default to engineer, architect, or a licensed trade that requires a specific credential unless the user explicitly asks for that profession or trade.",
  educacao:
    "No specialty was supplied. Choose a broadly representative education-support, training, tutoring, program, or classroom-support role appropriate to the selected level. Do not default to a licensed teacher or another regulated education profession unless the user explicitly asks for it.",
  financas:
    "No specialty was supplied. Choose a broadly representative finance/accounting support or analyst role appropriate to the selected level. Do not make a CPA, chartered-accountant, auditor-licence, or other professional designation mandatory unless the user explicitly asks for that professional track.",
  real_estate:
    "No specialty was supplied. Choose a broadly representative property-management, leasing-support, facilities, or real-estate operations role appropriate to the selected level. Do not default to a licensed real-estate salesperson, broker, appraiser, or other regulated profession unless the user explicitly asks for it.",
};

api.interceptors.request.use((config) => {
  // Privacy boundary: the original résumé file must never leave the browser.
  // Upload.jsx still contains a legacy pre-scan POST to /validate-document.
  // Short-circuit that request locally; sanitizeResume() immediately performs
  // the real extension/size/corruption/text-readability validation in-browser.
  // This adapter intentionally returns the same minimal success shape expected
  // by the legacy FileDrop code without issuing any network request.
  if (String(config?.url || "") === "/validate-document") {
    config.adapter = async () => ({
      data: { ok: true, validation: "browser-only" },
      status: 200,
      statusText: "OK",
      headers: {},
      config,
      request: null,
    });
  }

  // Re-practice is now owned by the lifecycle/daily-session module. Keep the
  // Report page API contract stable while routing around the retired legacy
  // credit/feature-flag endpoint.
  const requestUrl = String(config?.url || "");
  const repracticeMatch = requestUrl.match(/^\/sessions\/([^/]+)\/repractice$/);
  if (repracticeMatch && String(config?.method || "get").toLowerCase() === "post") {
    config.url = `/sessions/${repracticeMatch[1]}/repractice-v2`;
  }

  // Answer transcription/scoring must never leave the candidate trapped on an
  // infinite "Transcribing & scoring" state. Give normal AI processing ample
  // time, then let the response interceptor below verify whether the answer was
  // actually persisted before surfacing a retryable failure.
  if (
    /^\/sessions\/[^/]+\/answer$/.test(requestUrl) &&
    String(config?.method || "get").toLowerCase() === "post" &&
    !config.timeout
  ) {
    config.timeout = ANSWER_SCORING_TIMEOUT_MS;
  }

  // Sample-JD role-selection guard. This changes only the user-specified sample
  // path and only when the optional specialty/context field is blank. The
  // resume-derived path must remain driven solely by the reviewed resume.
  if (String(config?.url || "") === "/sample-jd" && config?.data) {
    let data = config.data;
    let serialized = false;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
        serialized = true;
      } catch {
        data = null;
      }
    }
    if (
      data &&
      typeof data === "object" &&
      String(data.generation_mode || "specified").toLowerCase() !== "resume" &&
      !String(data.extra_context || "").trim()
    ) {
      const guidance = SAMPLE_JD_DEFAULT_ROLE_GUIDANCE[String(data.category || "")];
      if (guidance) {
        const updated = { ...data, extra_context: guidance };
        config.data = serialized ? JSON.stringify(updated) : updated;
      }
    }
  }

  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      config.headers = config.headers || {};
      config.headers["X-User-Timezone"] = timezone;
    }
  } catch {
    // Backend falls back to UTC if the browser cannot resolve a timezone.
  }
  return config;
});

// MongoDB/Python UTC datetimes can arrive from admin endpoints as ISO strings
// without an explicit timezone suffix (for example 2026-10-14T21:35:50.316000).
// Browsers interpret those as local wall-clock time, which makes admin timestamps
// appear several hours late. Normalize only full datetime strings from /admin/*
// responses by marking timezone-less values as UTC. Existing Z/offset timestamps,
// date-only values, and non-admin payloads are left untouched.
const NAIVE_ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

function normalizeAdminUtcDatetimes(value) {
  if (typeof value === "string") {
    return NAIVE_ISO_DATETIME.test(value) ? `${value}Z` : value;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeAdminUtcDatetimes);
  }
  if (value && typeof value === "object") {
    for (const key of Object.keys(value)) {
      value[key] = normalizeAdminUtcDatetimes(value[key]);
    }
  }
  return value;
}

// Generated practice job descriptions use the same plain-English standard as
// interview questions. Corporate shorthand can be especially confusing for
// non-native English speakers, so remove a small, deterministic set of known
// idioms before the generated JD is shown or stored in form state.
function plainLanguageSampleJd(text) {
  if (typeof text !== "string" || !text) return text;

  let out = text;

  // "Own" and "drive" are acceptable everyday words, so only rewrite them
  // when they are being used as corporate action verbs at the start of a
  // sentence/bullet. This avoids changing phrases such as "their own work" or
  // literal driving requirements.
  out = out.replace(
    /(^|\n)(\s*(?:[-•]\s*)?)Own\b/g,
    (_match, prefix, bullet) => `${prefix}${bullet}Manage`,
  );
  out = out.replace(
    /(^|\n)(\s*(?:[-•]\s*)?)Drive\b/g,
    (_match, prefix, bullet) => `${prefix}${bullet}Lead`,
  );

  const phraseReplacements = [
    [/\bmove the needle\b/gi, "improve results"],
    [/\bhit the ground running\b/gi, "become productive quickly"],
    [/\bwear many hats\b/gi, "handle varied responsibilities"],
    [/\bstakeholder buy-in\b/gi, "stakeholder support"],
    [/\baudit-ready\b/gi, "ready for inspection"],
    [/\bsafety mindset\b/gi, "strong commitment to workplace safety"],
  ];
  for (const [pattern, replacement] of phraseReplacements) {
    out = out.replace(pattern, replacement);
  }

  return out;
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

api.interceptors.response.use(
  (response) => {
    const url = String(response?.config?.url || "");
    if (url.startsWith("/admin/")) {
      response.data = normalizeAdminUtcDatetimes(response.data);
    }
    if (url === "/sample-jd" && response?.data && typeof response.data.text === "string") {
      response.data.text = plainLanguageSampleJd(response.data.text);
    }

    // Re-practice is a permanent product feature now. Older report payloads and
    // legacy runtime configuration may still expose the retired launch flag as
    // false or omit it entirely. Normalize report responses so the Report page
    // bases eligibility on the real interview state (original/completed/unused)
    // rather than an obsolete rollout switch.
    if (/^\/sessions\/[^/]+\/report$/.test(url) && response?.data) {
      response.data.repractice_enabled = true;
    }

    return response;
  },
  async (error) => {
    const url = String(error?.config?.url || "");
    const answerMatch = url.match(/^\/sessions\/([^/]+)\/answer$/);
    const timedOut = error?.code === "ECONNABORTED" || /timeout/i.test(String(error?.message || ""));

    if (answerMatch && timedOut) {
      const sessionId = answerMatch[1];
      let questionIndex = null;
      try {
        const raw = error?.config?.data;
        if (raw && typeof raw.get === "function") {
          const value = raw.get("question_index");
          if (value !== null && value !== undefined && value !== "") {
            questionIndex = Number(value);
          }
        }
      } catch {
        questionIndex = null;
      }

      if (Number.isInteger(questionIndex)) {
        for (let attempt = 0; attempt < ANSWER_RECOVERY_ATTEMPTS; attempt += 1) {
          if (attempt > 0) await sleep(ANSWER_RECOVERY_DELAY_MS);
          try {
            const recovered = await api.get(`/sessions/${sessionId}`);
            const savedAnswer = (recovered?.data?.answers || []).find(
              (answer) => Number(answer?.question_index) === questionIndex,
            );
            if (savedAnswer) {
              return {
                data: {
                  transcript: savedAnswer.transcript || "",
                  evaluation: savedAnswer.evaluation || null,
                  recovered_after_timeout: true,
                },
                status: 200,
                statusText: "Recovered after scoring timeout",
                headers: {},
                config: error.config,
                request: error.request,
              };
            }
          } catch {
            // Keep polling briefly. If the answer never appears, the original
            // timeout is returned and the existing interview UI becomes retryable.
          }
        }
      }
    }

    return Promise.reject(error);
  },
);
